/**
 * Launchable projection + rail layout contract (见 21 / §5.7).
 *
 * Two platform capabilities live here, deliberately kept separate from the
 * sources that feed them:
 *
 * - **{@link LaunchItem}** — one normalized record for "something the user can
 *   start from the rail", projected from every source (module `activityBar`
 *   contributions, the platform's own built-in entries, app links). The unit is
 *   an *activity-bar item*, not a module: a module may contribute several
 *   entries, and a module with none is not an app.
 * - **rail layout** — which items are pinned, in what order, and which cannot be
 *   unpinned. Merged from ordered layers, persisted, broadcast.
 *
 * Its own channel rather than methods on `IModuleService`: the gated
 * `IGatedModuleService` extends that one, and both of these are in-process /
 * trusted-renderer concerns (same reasoning as `DETACH_CHANNEL` / `BUNDLE_CHANNEL`).
 */
import type { Disposer } from './event';
import type { IconSpec, RailPosition } from './index';

/** Cross-process channel for the launcher projection + rail layout. */
export const LAUNCHER_CHANNEL = 'launcher';

/**
 * EventBus event emitted whenever the projection or the layout changes.
 *
 * Deliberately **not** in `PLATFORM_PUBLIC_EVENTS`: the rail is trusted-shell
 * state, and the item list would tell a sandboxed surface what else is installed.
 */
export const LAUNCHER_CHANGED_EVENT = 'platform.launcher.changed';

/**
 * Module id the platform's own commands and launch items are attributed to. Not
 * a real module — it is what `%key%` titles resolve against (`platform:command.…`)
 * and what a face displays as the owner. `validateManifest` keeps real modules
 * out of the `workbench.` id namespace, so nothing can collide with these.
 */
export const PLATFORM_MODULE_ID = 'platform';

/** Id of the platform command that opens the preferences window (架构方案 §5.9 / 见 21). */
export const BUILTIN_OPEN_SETTINGS_COMMAND = 'workbench.openSettings';

/**
 * Alias of {@link BUILTIN_OPEN_SETTINGS_COMMAND}. Both open the same bare
 * preferences window; the rail only lists `platform:settings`.
 */
export const BUILTIN_OPEN_SETTINGS_WINDOW_COMMAND =
  'workbench.openSettingsWindow';

/**
 * Id of the platform command that opens a launchable (见 21 打开漏斗).
 *
 * Here rather than only in the renderer's built-in table because callers outside
 * a window address it too — the tray menu's open rows (需求 §4.6.2) run it over
 * `RUN_COMMAND_IPC`.
 */
export const BUILTIN_OPEN_COMMAND = 'workbench.open';

/** Argument shape for {@link BUILTIN_OPEN_COMMAND}: which launchable, by identity. */
export interface OpenCommandArgs {
  /**
   * `LaunchItem.key`（复合稳定键），**不是**把手 `im`。把手撞名时会被加后缀
   * （`im-launcher`），它是给人敲的显示文本、不是身份；命令参数要的是后者。
   */
  readonly key: string;
}

/**
 * How an `openUrl` launch is presented (见 21). Data, not code path: the entry
 * carries its hosting and the shell resolves it at click time, so adding a
 * hosting later changes one switch rather than every call site.
 *
 * `browser`, `inline`, and `window` are wired today; `miniapp` falls back to
 * the system browser with a visible degraded-state notice.
 */
export type LaunchHosting = 'browser' | 'inline' | 'window' | 'miniapp';

/**
 * What clicking a {@link LaunchItem} does. The first two mirror
 * `ActivityBarItemAction` one-for-one so a module item projects unchanged;
 * `openUrl` is the addition app links bring.
 */
export type LaunchAction =
  | { type: 'openView'; viewId: string; resolveInstance?: string }
  | { type: 'command'; command: string }
  | { type: 'openUrl'; url: string; hosting: LaunchHosting };

/** Where a {@link LaunchItem} came from; also the dedupe/priority axis. */
export type LaunchSource = 'builtin' | 'module' | 'app-link' | 'provider';

/** Dynamic status decoration rendered by the platform on a rail item. */
export type LaunchBadge =
  | { readonly kind: 'dot'; readonly label: string }
  | {
      readonly kind: 'count';
      readonly count: number;
      readonly label: string;
      /** Largest value rendered verbatim; larger values use `${max}+`. */
      readonly max?: number;
    };

/**
 * Module-owned dynamic badge source. The platform owns rendering and
 * cross-window invalidation; the module remains the source of business data.
 */
export interface LaunchBadgeProvider {
  read(): LaunchBadge | undefined;
  readonly onDidChange?: (listener: () => void) => Disposer;
}

/**
 * One launchable as a {@link LaunchItemProvider} hands it over (见 21). Layout
 * fields (`pinned` / `locked` / `order` / `segment`) are applied later by the
 * rail-layout merge — providers only supply the identity and the action.
 */
export interface ProvidedLaunchItem {
  readonly key: string;
  readonly title: string;
  readonly icon?: IconSpec;
  /** Selected glyph for default-rendered items; omitted keeps {@link icon}. */
  readonly iconActive?: IconSpec;
  readonly action?: LaunchAction;
  readonly customViewId?: string;
  /** Whether an expanded rail renders {@link title} beside the icon. */
  readonly showExpandedTitle?: boolean;
  /** Optional provider-owned badge; module items use a registered provider. */
  readonly badge?: LaunchBadge;
  readonly defaultSegment?: RailPosition;
  readonly defaultOrder?: number;
  readonly defaultPinned?: boolean;
}

/**
 * A pluggable source of arbitrary launchables (见 21), reserved for a future
 * app-center / catalogue module. Defined this round so the ModuleContext seam
 * exists; the platform does not ship a consumer yet.
 */
export interface LaunchItemProvider {
  readonly id: string;
  readonly priority: number;
  list(): Promise<readonly ProvidedLaunchItem[]>;
  readonly onDidChange?: (listener: () => void) => Disposer;
}

/**
 * One launchable, after projection and layout merge (见 21).
 *
 * `key` is `${moduleId}:${id}`. Item ids are namespaced by convention, but a
 * convention is not a guarantee — the composite key makes uniqueness structural
 * (the same reasoning 见 20 applies to module ids in an authority segment).
 */
export interface LaunchItem {
  readonly key: string;
  readonly source: LaunchSource;
  /** Owning module, or {@link PLATFORM_MODULE_ID} for built-in entries. */
  readonly moduleId: string;
  /**
   * Tooltip / label. A `%key%` placeholder for module items (resolved against
   * the owning module), a plain shell message key for `builtin` items, and a
   * literal user-entered string for app links.
   */
  readonly title: string;
  readonly icon?: IconSpec;
  /**
   * Selected glyph for default-rendered rail items. The renderer swaps to this
   * while the item is active; omitted keeps {@link icon} plus accent chrome.
   * `custom` and app-link items never read this field.
   */
  readonly iconActive?: IconSpec;
  readonly action?: LaunchAction;
  /**
   * Tooltip hint for the re-activation gesture (见 21), carried over from the
   * owning module's `activityBar` contribution. Only `module` items have it:
   * app-link and provider rows have no code in the renderer that could register
   * a handler, so the field would be dead weight there.
   */
  readonly reactivateHint?: string;
  /** Set when the owning module draws its own rail component (`custom: true`). */
  readonly customViewId?: string;
  /**
   * Whether an expanded rail renders {@link title} beside the icon. Omitted
   * means true; false keeps the title for tooltip / accessibility only.
   */
  readonly showExpandedTitle?: boolean;
  /** Dynamic status decoration rendered consistently by the platform. */
  readonly badge?: LaunchBadge;
  /** Rail section this item sits in. */
  readonly segment: RailPosition;
  /** Whether it currently shows in the rail. */
  readonly pinned: boolean;
  /**
   * Whether the user may unpin it. Set by a layout layer, **never** by the item's
   * source — a module declaring itself unremovable would be a squatting surface.
   * Monotonic: any layer setting it wins, so a preset can force presence while
   * the user still controls ordering.
   */
  readonly locked: boolean;
  /** Position inside {@link segment}, ascending. */
  readonly order: number;
}

/**
 * One layer of rail layout (见 21). Same shape as the four `workbench.rail.*`
 * product-config keys, so a deployment preset and a contributed preset are
 * literally the same data.
 */
export interface RailPreset {
  readonly top?: readonly string[];
  readonly middle?: readonly string[];
  readonly bottom?: readonly string[];
  /** Keys the user may not unpin. Monotonic across layers. */
  readonly locked?: readonly string[];
}

/**
 * A pluggable rail-layout preset contributed at runtime (见 21), e.g. a
 * role/space preset pushed by whichever module owns organisation identity. Shape
 * mirrors `ScopeLayer` (a bag of functions, no platform dependency) so the two
 * pluggable-layer mechanisms read the same.
 *
 * **Priority runs opposite to the configuration system on purpose.** There the
 * `workspace` layer (200) shadows `user` (100); here the *user* layer is always
 * highest and presets only supply initial values — otherwise "customizable"
 * would be a lie. A preset that must force something uses `locked`.
 */
export interface RailPresetLayer {
  readonly id: string;
  /** Merge order among presets; higher wins. Always below the user layer. */
  readonly priority: number;
  read(): RailPreset | undefined;
  readonly onDidChange?: (listener: () => void) => Disposer;
}

/**
 * The platform's own launch entries (见 21). They exist because code living in
 * the platform packages has no manifest to contribute through — that is the
 * *only* thing that distinguishes them. They carry **no privilege**: whether
 * they can be unpinned is decided by `workbench.rail.locked` exactly like a
 * module's entry.
 *
 * The one irreducible exception is a fail-safe, not a policy, and lives in the
 * renderer: if the layout cannot be read at all, these still render, or a broken
 * layout file would leave no way back into settings.
 */
export interface BuiltinLaunchEntry {
  readonly id: string;
  /** Shell message key (resolved with `t`, not the `%key%` module path). */
  readonly title: string;
  readonly icon: IconSpec;
  readonly action: LaunchAction;
  readonly segment: RailPosition;
}

export const BUILTIN_LAUNCH_ENTRIES: readonly BuiltinLaunchEntry[] = [
  {
    id: 'settings',
    title: 'workbench.action.settings',
    icon: { type: 'builtin', id: 'i-setting-9' },
    action: { type: 'command', command: BUILTIN_OPEN_SETTINGS_COMMAND },
    segment: 'bottom',
  },
];

/** Composite key for a launch item (`${moduleId}:${id}`). */
export function launchItemKey(moduleId: string, id: string): string {
  return `${moduleId}:${id}`;
}

/** Key of a built-in entry, for `workbench.rail.*` presets to reference. */
export function builtinLaunchKey(id: string): string {
  return launchItemKey(PLATFORM_MODULE_ID, id);
}

/**
 * Where a {@link LaunchItem}'s display label comes from: either text the user
 * typed, or a catalog key with the text to show when that key is missing.
 */
export type LaunchLabelSource =
  | { readonly kind: 'literal'; readonly text: string }
  | {
      readonly kind: 'message';
      readonly key: string;
      readonly fallback: string;
    };

/**
 * The label rule for a {@link LaunchItem} (见 21), without doing the lookup.
 *
 * Two faces resolve labels against different catalogs — the workbench through
 * its `Translator`, the host through `NlsService` for out-of-window chrome like
 * the tray — so the *rule* (a built-in title is a shell key, a module's is a
 * `%key%` scoped to its owner, an app-link's is literal) lives here rather than
 * being written twice.
 */
export function launchItemLabelSource(item: LaunchItem): LaunchLabelSource {
  if (item.source === 'builtin') {
    return { kind: 'message', key: item.title, fallback: item.title };
  }
  if (item.source === 'app-link' || item.source === 'provider') {
    return { kind: 'literal', text: item.title };
  }
  const inner = /^%(.+)%$/.exec(item.title)?.[1];
  return inner
    ? {
        kind: 'message',
        key: launchItemKey(item.moduleId, inner),
        fallback: inner,
      }
    : { kind: 'literal', text: item.title };
}

/**
 * In-process module face for the launcher (见 21): read the merged projection,
 * write the user layout layer. This is the seam `21` names for a catalogue module
 * — 「应用中心…消费 `context.launcher.*` 形状的查询 + RailLayout 写入」.
 *
 * Only `source` modules see a `ModuleContext` at all (§5.6 信任模型), like
 * {@link OverlayApi} / `window`, so a downloaded module cannot pin itself onto
 * the user's rail. Ungated beyond that: rail membership is a user-revocable
 * presentation preference, and the one thing a module must not be able to
 * override — `locked` — is refused by the registry rather than trusted here.
 *
 * Reading is as essential as writing: a caller offering "remove from nav" has to
 * know whether the item is `locked`, or it would offer an action the platform
 * then rejects.
 *
 * Reorder / reset stay out. They belong to the settings panel's full rail editor
 * over {@link ILauncherService}; a narrow face is easier to widen later than to
 * take back.
 */
export interface LauncherApi {
  /** The merged projection — the same list the rail renders from. */
  listItems(): Promise<LaunchItem[]>;
  /**
   * Pin / unpin one item in the user layer. Rejects when the item is `locked`.
   *
   * Changes broadcast {@link LAUNCHER_CHANGED_EVENT}; observe them with
   * `context.onEvent` instead of expecting a callback here.
   */
  setPinned(key: string, pinned: boolean): Promise<void>;
}

/**
 * Trusted-renderer RPC for the launcher (见 21). Reads the merged projection and
 * writes the user layout layer; the sources themselves are written elsewhere
 * (app links through `IAppLinkService`, module items through their manifests).
 */
export interface ILauncherService {
  listItems(): Promise<LaunchItem[]>;
  setPinned(key: string, pinned: boolean): Promise<void>;
  /** Replace the order of one rail section with `keys` (user layer). */
  reorder(segment: RailPosition, keys: readonly string[]): Promise<void>;
  /** Drop the user layer entirely, falling back to the presets. */
  resetLayout(): Promise<void>;
  /**
   * Open a URL outside the app (见 21). Desktop path: relays to the main
   * process's `shell.openExternal`. The browser path prefers a synchronous
   * renderer-side open (to keep the user gesture); this method is the fallback
   * when no such bridge is injected.
   */
  openExternal(url: string): Promise<void>;
}
